import { z } from 'zod';

const ID_BODY = '[0-9A-Za-z]{1,64}';

function idSchema<B extends string>(prefix: string) {
  return z
    .string()
    .regex(new RegExp(`^${prefix}_${ID_BODY}$`), `expected an id of the form ${prefix}_...`)
    .brand<B>();
}

export const ID_PREFIXES = {
  tenant: 'tnt',
  organization: 'org',
  user: 'usr',
  apiKey: 'key',
  session: 'sess',
  conversation: 'conv',
  message: 'msg',
  run: 'run',
  file: 'file',
  toolCallAudit: 'tca',
  request: 'req',
  testAccount: 'tac',
  evalCase: 'evc',
  evalRun: 'evr',
  scheduledTask: 'stk',
  scheduledTaskRun: 'str',
  consoleAccount: 'cacc',
  consoleInvite: 'cinv',
  skill: 'skl',
  personalSkill: 'psk',
} as const;

export type IdKind = keyof typeof ID_PREFIXES;

export const TenantIdSchema = idSchema<'TenantId'>(ID_PREFIXES.tenant);
export type TenantId = z.infer<typeof TenantIdSchema>;

export const OrganizationIdSchema = idSchema<'OrganizationId'>(ID_PREFIXES.organization);
export type OrganizationId = z.infer<typeof OrganizationIdSchema>;

export const UserIdSchema = idSchema<'UserId'>(ID_PREFIXES.user);
export type UserId = z.infer<typeof UserIdSchema>;

export const ApiKeyIdSchema = idSchema<'ApiKeyId'>(ID_PREFIXES.apiKey);
export type ApiKeyId = z.infer<typeof ApiKeyIdSchema>;

export const SessionIdSchema = idSchema<'SessionId'>(ID_PREFIXES.session);
export type SessionId = z.infer<typeof SessionIdSchema>;

export const ConversationIdSchema = idSchema<'ConversationId'>(ID_PREFIXES.conversation);
export type ConversationId = z.infer<typeof ConversationIdSchema>;

export const MessageIdSchema = idSchema<'MessageId'>(ID_PREFIXES.message);
export type MessageId = z.infer<typeof MessageIdSchema>;

export const RunIdSchema = idSchema<'RunId'>(ID_PREFIXES.run);
export type RunId = z.infer<typeof RunIdSchema>;

export const FileIdSchema = idSchema<'FileId'>(ID_PREFIXES.file);
export type FileId = z.infer<typeof FileIdSchema>;

export const ToolCallAuditIdSchema = idSchema<'ToolCallAuditId'>(ID_PREFIXES.toolCallAudit);
export type ToolCallAuditId = z.infer<typeof ToolCallAuditIdSchema>;

export const RequestIdSchema = idSchema<'RequestId'>(ID_PREFIXES.request);
export type RequestId = z.infer<typeof RequestIdSchema>;

export const TestAccountIdSchema = idSchema<'TestAccountId'>(ID_PREFIXES.testAccount);
export type TestAccountId = z.infer<typeof TestAccountIdSchema>;

export const EvalCaseIdSchema = idSchema<'EvalCaseId'>(ID_PREFIXES.evalCase);
export type EvalCaseId = z.infer<typeof EvalCaseIdSchema>;

export const EvalRunIdSchema = idSchema<'EvalRunId'>(ID_PREFIXES.evalRun);
export type EvalRunId = z.infer<typeof EvalRunIdSchema>;

export const ScheduledTaskIdSchema = idSchema<'ScheduledTaskId'>(ID_PREFIXES.scheduledTask);
export type ScheduledTaskId = z.infer<typeof ScheduledTaskIdSchema>;

export const ScheduledTaskRunIdSchema = idSchema<'ScheduledTaskRunId'>(
  ID_PREFIXES.scheduledTaskRun,
);
export type ScheduledTaskRunId = z.infer<typeof ScheduledTaskRunIdSchema>;

export const ConsoleAccountIdSchema = idSchema<'ConsoleAccountId'>(ID_PREFIXES.consoleAccount);
export type ConsoleAccountId = z.infer<typeof ConsoleAccountIdSchema>;

export const ConsoleInviteIdSchema = idSchema<'ConsoleInviteId'>(ID_PREFIXES.consoleInvite);
export type ConsoleInviteId = z.infer<typeof ConsoleInviteIdSchema>;

export const SkillIdSchema = idSchema<'SkillId'>(ID_PREFIXES.skill);
export type SkillId = z.infer<typeof SkillIdSchema>;

export const PersonalSkillIdSchema = idSchema<'PersonalSkillId'>(ID_PREFIXES.personalSkill);
export type PersonalSkillId = z.infer<typeof PersonalSkillIdSchema>;

export const UserSubjectSchema = z.string().min(1).max(256).brand<'UserSubject'>();
export type UserSubject = z.infer<typeof UserSubjectSchema>;

export const TEST_ACCOUNT_SUBJECT_PREFIX = 'plato-test:';

export const McpServerNameSchema = z
  .string()
  .regex(
    /^[a-z0-9](?:[a-z0-9-]{0,30}[a-z0-9])?$/,
    'expected a lower-case server name such as "acme"',
  )
  .brand<'McpServerName'>();
export type McpServerName = z.infer<typeof McpServerNameSchema>;

export const ToolCallIdSchema = z.string().min(1).max(256).brand<'ToolCallId'>();
export type ToolCallId = z.infer<typeof ToolCallIdSchema>;

interface IdTypes {
  tenant: TenantId;
  organization: OrganizationId;
  user: UserId;
  apiKey: ApiKeyId;
  session: SessionId;
  conversation: ConversationId;
  message: MessageId;
  run: RunId;
  file: FileId;
  toolCallAudit: ToolCallAuditId;
  request: RequestId;
  testAccount: TestAccountId;
  evalCase: EvalCaseId;
  evalRun: EvalRunId;
  scheduledTask: ScheduledTaskId;
  scheduledTaskRun: ScheduledTaskRunId;
  consoleAccount: ConsoleAccountId;
  consoleInvite: ConsoleInviteId;
  skill: SkillId;
  personalSkill: PersonalSkillId;
}

const ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
const ID_RANDOM_LENGTH = 24;

function randomBody(length: number): string {
  // Rejection sampling keeps the distribution uniform over the 62 characters.
  const limit = 256 - (256 % ALPHABET.length);
  let out = '';
  while (out.length < length) {
    const bytes = new Uint8Array(length * 2);
    globalThis.crypto.getRandomValues(bytes);
    for (const byte of bytes) {
      if (byte < limit && out.length < length) out += ALPHABET[byte % ALPHABET.length];
    }
  }
  return out;
}

export function newId<K extends IdKind>(kind: K): IdTypes[K] {
  return `${ID_PREFIXES[kind]}_${randomBody(ID_RANDOM_LENGTH)}` as IdTypes[K];
}

const ID_SCHEMAS = {
  tenant: TenantIdSchema,
  organization: OrganizationIdSchema,
  user: UserIdSchema,
  apiKey: ApiKeyIdSchema,
  session: SessionIdSchema,
  conversation: ConversationIdSchema,
  message: MessageIdSchema,
  run: RunIdSchema,
  file: FileIdSchema,
  toolCallAudit: ToolCallAuditIdSchema,
  request: RequestIdSchema,
  testAccount: TestAccountIdSchema,
  evalCase: EvalCaseIdSchema,
  evalRun: EvalRunIdSchema,
  scheduledTask: ScheduledTaskIdSchema,
  scheduledTaskRun: ScheduledTaskRunIdSchema,
  consoleAccount: ConsoleAccountIdSchema,
  consoleInvite: ConsoleInviteIdSchema,
  skill: SkillIdSchema,
  personalSkill: PersonalSkillIdSchema,
} as const;

export function parseId<K extends IdKind>(kind: K, value: unknown): IdTypes[K] {
  return ID_SCHEMAS[kind].parse(value) as IdTypes[K];
}

export function isId<K extends IdKind>(kind: K, value: unknown): value is IdTypes[K] {
  return ID_SCHEMAS[kind].safeParse(value).success;
}

export const KEY_PREFIXES = {
  publishableTest: 'plato_pk_test_',
  publishableLive: 'plato_pk_live_',
  secretTest: 'plato_sk_test_',
  secretLive: 'plato_sk_live_',
  clientSecret: 'ps_',
} as const;

export type KeyMode = 'test' | 'live';
